class FreeShippingProgress extends HTMLElement {
  constructor() {
    super();
    this.refreshTimer = null;
    this.refreshSequence = 0;
    this.observer = null;
  }

  connectedCallback() {
    this.threshold = Number(this.dataset.threshold || 0);
    this.message = this.querySelector('[data-free-shipping-message]');
    this.track = this.querySelector('[data-free-shipping-track]');
    this.bar = this.querySelector('[data-free-shipping-bar]');
    this.section = this.closest('.free-shipping-progress-section');

    if (window.TFBTradeDeliveryConfig?.isB2BCustomer) {
      if (this.section) this.section.hidden = true;
      return;
    }

    if (!this.threshold || !this.message || !this.track || !this.bar) return;

    this.update(Number(this.dataset.cartTotal || 0));
    this.observeCartChanges();
    this.refresh();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.refreshTimer) window.clearTimeout(this.refreshTimer);
  }

  observeCartChanges() {
    const targets = [
      document.getElementById('main-cart-items'),
      document.getElementById('main-cart-footer'),
    ].filter(Boolean);

    if (targets.length === 0) return;

    this.observer = new MutationObserver(() => this.scheduleRefresh());
    targets.forEach((target) => {
      this.observer.observe(target, { childList: true, subtree: true });
    });
  }

  scheduleRefresh() {
    if (this.refreshTimer) window.clearTimeout(this.refreshTimer);
    this.refreshTimer = window.setTimeout(() => this.refresh(), 150);
  }

  async refresh() {
    if (window.TFBTradeDeliveryConfig?.isB2BCustomer) {
      if (this.section) this.section.hidden = true;
      return;
    }

    const sequence = ++this.refreshSequence;
    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) throw new Error('Unable to refresh the cart total.');

      const cart = await response.json();
      if (sequence !== this.refreshSequence) return;

      const hasTradeItems = cart.items.some((item) =>
        typeof item.sku === 'string' && item.sku.trim().toUpperCase().endsWith('-TRADE')
      );

      if (hasTradeItems) {
        if (this.section) this.section.hidden = true;
        return;
      }

      if (this.section) this.section.hidden = false;

      const deliveryCharge = cart.items.reduce((total, item) => {
        const isCharge =
          String(item.sku || '').trim().toUpperCase() === 'TFB-TRADE-DELIVERY' ||
          String(item.handle || '') === 'trade-delivery-charge-8';
        return isCharge ? total + Number(item.final_line_price || 0) : total;
      }, 0);

      this.update(Math.max(0, Number(cart.total_price || 0) - deliveryCharge));
    } catch (error) {
      console.error(error);
    }
  }

  formatMoney(cents) {
    const hasPence = cents % 100 !== 0;

    return new Intl.NumberFormat(this.dataset.locale || 'en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: hasPence ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  update(cartTotal) {
    const safeTotal = Math.max(0, cartTotal);
    const amountRemaining = Math.max(0, this.threshold - safeTotal);
    const progress = Math.min(100, (safeTotal / this.threshold) * 100);

    if (safeTotal === 0) {
      this.message.textContent = `Free UK delivery when you spend ${this.formatMoney(this.threshold)}`;
    } else if (amountRemaining > 0) {
      this.message.textContent = `Add ${this.formatMoney(amountRemaining)} more for free UK delivery`;
    } else {
      this.message.textContent = 'You have unlocked free UK delivery';
    }

    this.bar.style.width = `${progress}%`;
    this.track.setAttribute('aria-valuenow', String(Math.min(safeTotal, this.threshold)));
  }
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
}
